import { Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { Observable, map, switchMap, forkJoin, of } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class ApolloClassService {

    constructor(private apollo: Apollo) { }

    getClassList(): Observable<any[]> {
        const GET_CLASSLIST = gql`
            query {
                class {
                    allClass(pageNo: 1) {
                        count
                        pageNo
                        pageSize
                        totalPages
                        data {
                            id
                            name
                            grade {
                                id
                                name
                                isStaffSpecificStudent
                            }
                            school {
                                id
                                name
                                description
                                address
                                postalCode
                                city
                                state {
                                    id
                                    name
                                }
                                weekend {
                                    id
                                    type
                                }
                            }
                            schoolYear {
                                year
                                month
                            }
                            classStaff {
                                id
                                userRole {
                                    id
                                    user {
                                        email
                                        firstName
                                        lastName
                                        phoneNumber
                                    }
                                    refRole {
                                        name
                                    }
                                }
                            }
                            studentClass {
                                id
                                student {
                                    id
                                    firstName
                                    lastName
                                    gender
                                    birthCertificateNo
                                    myKidNo
                                    dateOfBirth
                                    isActive
                                    isOku
                                    refOkuType {
                                        name
                                        description
                                    }
                                    user {
                                        email
                                        firstName
                                        lastName
                                        phoneNumber
                                        userRoles {
                                            refRole {
                                                name
                                            }
                                        }
                                    }
                                    masterPayment {
                                        id
                                        year
                                        fees {
                                            id
                                            name
                                            amount
                                            isMandatory
                                            discountType {
                                                name
                                                value
                                                type
                                            }
                                            refFeesType {
                                                id
                                                name
                                            }
                                            otCalculation {
                                                name
                                                type
                                                calculationType
                                                isOverTime
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        `;

        return this.apollo.watchQuery<any>({
            query: GET_CLASSLIST,
            fetchPolicy: 'network-only',
        }).valueChanges.pipe(
            map(result => {
                const classListData = result?.data?.class?.allClass?.data || [];
                return classListData;
            })
        );
    }

    // Fetch a single page of classes
    private getClassListPage(pageNo: number): Observable<any> {
        const GET_CLASSLIST = gql`
            query {
                class {
                    allClass(pageNo: ${pageNo}) {
                        count
                        pageNo
                        pageSize
                        totalPages
                        data {
                            id
                            name
                            isActive
                        }
                    }
                }
            }
        `;

        return this.apollo.query<any>({
            query: GET_CLASSLIST,
            fetchPolicy: 'network-only',
        }).pipe(
            map(result => {
                const response = result?.data?.class?.allClass;
                return {
                    data: response?.data || [],
                    totalPages: response?.totalPages || 1,
                    pageNo: response?.pageNo || 1,
                    count: response?.count || 0
                };
            })
        );
    }

    // Fetch all pages of classes
    getAllClassList(): Observable<any[]> {
        return this.getClassListPage(1).pipe(
            switchMap((firstPage: any) => {
                const totalPages = firstPage.totalPages || 1;
                if (totalPages <= 1) {
                    return of(firstPage.data || []);
                }

                // Fetch all remaining pages
                const pageObservables: Observable<any>[] = [of(firstPage.data || [])];
                for (let page = 2; page <= totalPages; page++) {
                    pageObservables.push(
                        this.getClassListPage(page).pipe(
                            map((pageResult: any) => pageResult.data || [])
                        )
                    );
                }

                return forkJoin(pageObservables).pipe(
                    map((pages: any[][]) => {
                        return pages.flat();
                    })
                );
            })
        );
    }

    createClass(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: null`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const CREATE_CLASS = gql`
            mutation {
                class {
                    addClass(input: { ${inputString} }){
                        success
                        error
                    }
                }
            }
        `;

        return this.apollo.mutate<any>({
            mutation: CREATE_CLASS
        }).pipe(
            map(result => {
                const classDetail = result?.data?.class?.addClass;
                return classDetail;
            })
        );
    }


    updateClass(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: null`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const UPDATE_CLASS = gql`
            mutation {
                class {
                    updateClass(input: { ${inputString} }){
                        success
                        error
                    }
                }
            }
        `;

        return this.apollo.mutate<any>({
            mutation: UPDATE_CLASS
        }).pipe(
            map(result => {
                const classDetail = result?.data?.class?.updateClass;
                return classDetail;
            })
        );
    }


    deleteClass(id: number): Observable<any> {
        const DELETE_CLASS = gql`
            mutation {
                class {
                    deleteClass(input:  {
                        id: ${id},
                    }){
                        success
                        error
                    }
                }
            }
        `;

        return this.apollo.mutate<any>({
            mutation: DELETE_CLASS,
        }).pipe(
            map(result => {
                const classDetail = result?.data?.class?.deleteClass;
                return classDetail;
            })
        );
    }


    getClassById(id: number): Observable<any> {
        const GET_CLASS = gql`
            query {
                class {
                    classById(id: ${id}) {
                        id
                        name
                        grade {
                            id
                            name
                            isStaffSpecificStudent
                        }
                        school {
                            id
                            name
                            description
                            address
                            postalCode
                            city
                            state {
                                id
                                name
                            }
                            weekend {
                                id
                                type
                            }
                        }
                        schoolYear {
                            year
                            month
                        }
                        classStaff {
                            id
                            userRole {
                                id
                                user {
                                    email
                                    firstName
                                    lastName
                                    phoneNumber
                                }
                                refRole {
                                    name
                                }
                            }
                        }
                        studentClass {
                            id
                            student {
                                id
                                firstName
                                lastName
                                gender
                                birthCertificateNo
                                myKidNo
                                dateOfBirth
                                isActive
                                isOku
                                refOkuType {
                                    name
                                    description
                                }
                                user {
                                    email
                                    firstName
                                    lastName
                                    phoneNumber
                                    userRoles {
                                        refRole {
                                            name
                                        }
                                    }
                                }
                            }
                        }
                        }
                }
            }
        `;

        return this.apollo.query<any>({
            query: GET_CLASS,
            variables: { id: id },
            fetchPolicy: 'network-only',
        }).pipe(
            map(result => {
                const classDetail = result?.data?.class?.classById[0];
                return classDetail;
            })
        );
    }
    

    private removeTypename(data: any): any {
        if (Array.isArray(data)) {
            return data.map(item => this.removeTypename(item));
        } else if (data !== null && typeof data === 'object') {
            const newObj: any = {};
            Object.keys(data).forEach(key => {
                if (key !== '__typename') {
                    newObj[key] = this.removeTypename(data[key]);
                }
            });
            return newObj;
        }
        return data;
    }
}
